import { Box, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EDIT_ICON_GRAY from '../../../../assets/svg/edit-icon-gray.svg';
import CustomSwitch from '../../../../components/Switch/Switch';
import PreOrderModal, { PreOrderModalSubmitData } from '../../../../components/PreOrderModal/PreOrderModal';
import { LANG_TEXT } from '../../../../constants/language';
import { useMenuContext } from '../../../../context/MenuContext';
import { useSettingsContext } from '../../../../context/SettingsContext';
import { usePreorderRules } from '../../../../context/PreorderRulesContext/PreorderRulesContext';
import { usePreorderEnabled } from '../../../../hooks/usePreorderEnabled';
import usePermission from '../../../../hooks/usePermission';
import { updateDoc } from '../../../../libs/firestore-wrapper';
import { getFoodDetails, getCurrentSettings } from '../../../../modals/order';
import { formatPriceItem } from '../../../../utils';
import { IFoodDetails } from '../../menu.types';
import { useStyles } from './styles';
import { PreorderRuleState } from '../../../PreoderRules/PreorderRules.types';
import { convertAPIToPreorderRuleState, getStadiumPreorderRuleState } from '../../../../utils/preorderRules';

function Card({ food, toggleDrawer, bulkPreOrderEnabled }: { food: IFoodDetails; toggleDrawer: (open: boolean, data: IFoodDetails) => void; bulkPreOrderEnabled?: boolean }) {
  const classes = useStyles();
  const { setFoodMenu } = useMenuContext();
  const { enabledNewVersion } = usePreorderEnabled();
  const { currentSetting, setCurrentSetting, stadiumData } = useSettingsContext();
  const { currentValues: preorderRulesValues } = usePreorderRules();
  const { restaurantId } = usePermission();
  const { t } = useTranslation();

  const [preOrderModal, setPreOrderModal] = useState(false);

  const masterPreorderRule = useMemo(
    (): PreorderRuleState => getStadiumPreorderRuleState(stadiumData, preorderRulesValues.preorderRule),
    [stadiumData, preorderRulesValues.preorderRule]
  );

  const preOrderInitialRule = useMemo((): PreorderRuleState => {
    return convertAPIToPreorderRuleState(currentSetting?.preorder_rules, masterPreorderRule);
  }, [currentSetting?.preorder_rules, masterPreorderRule]);

  const handleAvailableChange = (isChecked: boolean, foodId: string) => {
    food.enabled = isChecked;
    setFoodMenu((prevStates) => ({
      ...prevStates,
      foods: prevStates.foods.map((foodItem) => {
        if (foodItem.id === foodId) {
          return { ...foodItem, enabled: isChecked };
        }
        return foodItem;
      }),
    }));
    updateDoc(getFoodDetails(foodId), { enabled: isChecked });
  };

  const applyPreOrderChange = (foodId: string, isChecked: boolean) => {
    food.enabled_pre_order = isChecked;
    setFoodMenu((prevStates) => ({
      ...prevStates,
      foods: prevStates.foods.map((foodItem) => {
        if (foodItem.id === foodId) {
          return { ...foodItem, enabled_pre_order: isChecked };
        }
        return foodItem;
      }),
    }));
    updateDoc(getFoodDetails(foodId), { enabled_pre_order: isChecked });
  };

  const handlePreOrderChange = (isChecked: boolean, foodId: string) => {
    if (isChecked) {
      if (bulkPreOrderEnabled) {
        // Bulk pre-order is already ON → settings already configured, apply directly
        applyPreOrderChange(foodId, true);
      } else {
        // Bulk pre-order is OFF → need to configure email + rules first
        setPreOrderModal(true);
      }
    } else {
      applyPreOrderChange(foodId, false);
    }
  };

  const handlePreOrderModalConfirm = async (formData: PreOrderModalSubmitData) => {
    const emailsArray = formData.emails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const { preorderRule } = formData;
    const unitToType = (unit: string) => {
      if (unit === 'minute') return 'minutes';
      if (unit === 'hour') return 'hours';
      if (unit === 'day') return 'days';
      return 'days';
    };

    const acceptance = {
      since: {
        at: preorderRule.acceptanceSince.pickerValue,
        time: preorderRule.acceptanceSince.value,
        type: unitToType(preorderRule.acceptanceSince.unit),
      },
      until: {
        at: preorderRule.acceptanceUntil.pickerValue,
        time: preorderRule.acceptanceUntil.value,
        type: unitToType(preorderRule.acceptanceUntil.unit),
      },
    };
    const orderDeliverable = {
      since: {
        time: 0,
        type: 'minutes',
      },
      until: {
        time: preorderRule.orderDeliverableUntil.value,
        type: unitToType(preorderRule.orderDeliverableUntil.unit),
      },
    };

    await updateDoc(getCurrentSettings(restaurantId), {
      'preorder_rules.pre_order_email_addresses': emailsArray,
      'preorder_rules.acceptance': acceptance,
      'preorder_rules.order_deliberable': orderDeliverable,
    });

    setCurrentSetting((prev: any) => ({
      ...prev,
      preorder_rules: {
        ...(prev.preorder_rules || {}),
        pre_order_email_addresses: emailsArray,
        acceptance,
        order_deliberable: orderDeliverable,
      },
    }));

    setPreOrderModal(false);
    applyPreOrderChange(food.id, true);
  };

  return (
    <Box className={classes.BoxCard}>
      <img className={classes.Img} src={food.image_url} alt={food.image_url} loading="lazy" />
      <Box className={classes.BoxDetails}>
        <Box className={classes.BoxTitle}>
          <Typography fontSize={16} lineHeight={'24px'} className={classes.FoodName}>
            {food.name}
          </Typography>
          <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} color={'var(--Primary-500, #3D9F4A)'}>
            ¥ {formatPriceItem(food.price)}
          </Typography>
        </Box>
        <Box className={classes.BoxAction}>
          <img
            onClick={() => toggleDrawer(true, food)}
            style={{ color: 'rgba(52, 64, 84, 1)', cursor: 'pointer' }}
            src={EDIT_ICON_GRAY}
            height={24}
            width={24}
            alt="EDIT_ICON_GRAY"
          />
          <CustomSwitch
            onChange={(isChecked: boolean) => handleAvailableChange(isChecked, food.id)}
            isChecked={food.enabled ?? false}
            label={t(LANG_TEXT.AVAILABLE)}
            className={classes.Switch}
          />
          {enabledNewVersion && (
            <CustomSwitch
              onChange={(isChecked: boolean) => handlePreOrderChange(isChecked, food.id)}
              isChecked={food.enabled_pre_order ?? false}
              label={t(LANG_TEXT.PRE_ORDER)}
              className={classes.Switch}
              disabled={false}
            />
          )}
        </Box>
      </Box>

      <PreOrderModal
        open={preOrderModal}
        handleClose={() => setPreOrderModal(false)}
        handleSubmit={handlePreOrderModalConfirm}
        initialRule={preOrderInitialRule}
        initialEmails={(currentSetting?.preorder_rules?.pre_order_email_addresses || []).join(', ')}
        masterRule={masterPreorderRule}
      />
    </Box>
  );
}

export default Card;
